import { generateSalt } from "@passwordflow/crypto";
import type { AdminUser } from "../api/admin.js";
import {
  createInstanceKey,
  enrollAdminRecovery,
  fetchEscrow,
  grantInstanceKey,
  recoverUser
} from "../api/recovery.js";
import type { AdminRecoveryStatus, RecoveryAdmin, RecoveryOverview, RecoveryRequest } from "../api/recovery.js";
import { bytesToB64 } from "../b64.js";
import { ClientError } from "../cryptoClient.js";
import type { CryptoClient } from "../cryptoClient.js";
import { AuthFlowError, generateTempPassword } from "./authFlow.js";
import { ensureIdentity } from "./teamKeys.js";

/** Worker failures become a message the admin can act on; everything else passes through. */
async function inWorker<T>(message: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err: unknown) {
    if (err instanceof ClientError) {
      throw new AuthFlowError("admin-recovery", message);
    }
    throw err;
  }
}

/**
 * Sets up the instance recovery key. The key pair is generated in this browser; the server gets
 * the public half and the secret half sealed to our own identity key.
 */
export async function setUpInstanceKey(client: CryptoClient): Promise<RecoveryOverview | null> {
  await ensureIdentity(client);
  const created = await inWorker("Der Schlüssel konnte nicht erzeugt werden.", (): Promise<{ publicKeyB64: string; sealedKeyB64: string }> => client.instanceKeyCreate());
  return createInstanceKey(created.publicKeyB64, created.sealedKeyB64);
}

/** Opens our grant of the instance key in the worker and checks it against the published public key. */
async function loadInstanceKey(client: CryptoClient, overview: RecoveryOverview): Promise<void> {
  const sealed = overview.sealedKeyB64;
  if (sealed === null) {
    throw new AuthFlowError("admin-recovery", "Du hältst den Wiederherstellungsschlüssel der Instanz nicht. Bitte einen Administrator, der ihn hat, ihn dir freizugeben.");
  }
  await ensureIdentity(client);
  await inWorker(
    "Dein Anteil am Wiederherstellungsschlüssel passt nicht zum Schlüssel der Instanz. Lass ihn dir neu freigeben.",
    (): Promise<void> => client.instanceKeyOpen(overview.publicKeyB64, sealed)
  );
}

/** Hands the instance key to another admin: sealed to their identity key in this browser. */
export async function grantInstanceKeyTo(client: CryptoClient, overview: RecoveryOverview, admin: RecoveryAdmin): Promise<RecoveryOverview | null> {
  const recipient = admin.identityPubB64;
  if (recipient === null) {
    throw new AuthFlowError("admin-recovery", `${admin.email} hat noch kein Schlüsselpaar. Es entsteht, sobald das Konto den Bereich „Wiederherstellung“ einmal öffnet.`);
  }
  await loadInstanceKey(client, overview);
  const sealed = await inWorker("Der Schlüssel konnte nicht versiegelt werden.", (): Promise<string> => client.instanceKeySealFor(recipient));
  return grantInstanceKey(admin.userId, sealed);
}

export interface RecoveredAccount {
  readonly request: RecoveryRequest;
  /** The one-time password, shown once to hand over in person. The server never sees it. */
  readonly password: string;
}

/**
 * Resets an enrolled account: opens its escrow with the instance key, seals the account keys under
 * a new one-time password and stores that. The owner then signs in with it and picks a new master
 * password; every item stays readable.
 */
export async function recoverAccount(client: CryptoClient, overview: RecoveryOverview, user: AdminUser): Promise<RecoveredAccount> {
  await loadInstanceKey(client, overview);
  const escrow = await fetchEscrow(user.id);
  const password = generateTempPassword();
  const saltB64 = bytesToB64(generateSalt(16));
  const sealed = await inWorker(
    "Die Wiederherstellungsdaten des Kontos lassen sich nicht öffnen.",
    (): Promise<{ authKeyB64: string; keyEncB64: string }> => client.escrowReset(escrow.escrowB64, password, saltB64, user.email)
  );
  const request = await recoverUser(user.id, { keyId: escrow.keyId, saltB64, authKeyB64: sealed.authKeyB64, keyEncB64: sealed.keyEncB64 });
  return { request, password };
}

/** Enrolls our account: the loaded account keys sealed to the instance recovery public key. */
export async function joinAdminRecovery(client: CryptoClient, status: AdminRecoveryStatus): Promise<void> {
  const escrowB64 = await inWorker("Die Registrierung ist fehlgeschlagen.", (): Promise<string> => client.sealEscrow(status.publicKeyB64));
  await enrollAdminRecovery(status.keyId, escrowB64);
}

/** Whether this session should enroll on its own: the policy requires it and the account is not enrolled yet. */
export function needsAutoEnroll(status: AdminRecoveryStatus | null, required: boolean): status is AdminRecoveryStatus {
  return status !== null && required && !status.enrolled;
}
